import React, { useEffect, useState } from "react";

// MUI
import {
    Avatar,
    Box,
    Container,
    Typography,
    Card,
    CardContent,
    Stack,
    Chip,
    useTheme,
    Divider,
    Paper
} from '@mui/material'
import {
    CheckCircle,
    Person,
    Cake,
    School,
    CalendarToday,
    LocationOn,
    VerifiedUser,
    Badge as BadgeIcon,
    People,
    Shield
} from '@mui/icons-material'

export default function VerifiedCertificate({ apipublic, snackbar, stuimg }) {
    const theme = useTheme();
    const enrollment = new URLSearchParams(window.location.search).get('enrollment')

    const [info, setInfo] = useState([])
    useEffect(() => {
        var formData = new FormData()
        formData.append('verify-certificate', enrollment)
        fetch(apipublic, {
            method: 'post',
            body: formData
        }).then(res => res.json())
            .then(data => {
                if (data.status == false) {
                    snackbar(data.message)
                } else {
                    setInfo(data.data)
                }
            })
    }, [])

    return (
        <>
            {/* Hero Section */}
            <Box
                sx={{
                    background: `linear-gradient(135deg, ${theme.palette.primary.main} 0%, ${theme.palette.primary.dark} 100%)`,
                    pt: { xs: 10, md: 12 },
                    pb: { xs: 8, md: 10 },
                    position: 'relative',
                    overflow: 'hidden'
                }}
            >
                <Container maxWidth="lg">
                    <Stack spacing={4} alignItems="center">
                        {/* Verification Badge */}
                        <Box
                            sx={{
                                display: 'flex',
                                alignItems: 'center',
                                gap: 1.5,
                                px: 3,
                                py: 1,
                                borderRadius: 50,
                                background: 'rgba(255, 255, 255, 0.2)',
                                backdropFilter: 'blur(10px)',
                                border: '1px solid rgba(255, 255, 255, 0.3)'
                            }}
                        >
                            <CheckCircle sx={{ color: '#fff', fontSize: '1.2rem' }} />
                            <Typography
                                sx={{
                                    color: '#fff',
                                    fontWeight: 700,
                                    fontSize: { xs: '0.85rem', md: '0.95rem' },
                                    letterSpacing: '0.5px'
                                }}
                            >
                                VERIFIED CERTIFICATE
                            </Typography>
                        </Box>

                        {/* Student Avatar with Shield */}
                        <Box sx={{ position: 'relative' }}>
                            <Avatar
                                sx={{
                                    width: { xs: 140, md: 180 },
                                    height: { xs: 140, md: 180 },
                                    border: '6px solid #fff',
                                    boxShadow: '0 8px 32px rgba(0, 0, 0, 0.2)'
                                }}
                                src={`${stuimg + info.studentid}.jpg`}
                            />
                            <Box
                                sx={{
                                    position: 'absolute',
                                    bottom: -5,
                                    right: -5,
                                    width: 50,
                                    height: 50,
                                    borderRadius: '50%',
                                    background: theme.palette.success.main,
                                    display: 'flex',
                                    alignItems: 'center',
                                    justifyContent: 'center',
                                    border: '4px solid #fff',
                                    boxShadow: '0 4px 12px rgba(0, 0, 0, 0.15)'
                                }}
                            >
                                <Shield sx={{ color: '#fff', fontSize: '1.5rem' }} />
                            </Box>
                        </Box>

                        {/* Main Heading */}
                        <Stack spacing={2} alignItems="center">
                            <Typography
                                sx={{
                                    fontSize: { xs: '2rem', md: '3rem' },
                                    fontWeight: 800,
                                    color: '#fff',
                                    textAlign: 'center',
                                    lineHeight: 1.1
                                }}
                            >
                                Certificate Verified Successfully
                            </Typography>
                            <Chip
                                label={`Enrollment: ${enrollment}`}
                                sx={{
                                    background: 'rgba(255, 255, 255, 0.25)',
                                    color: '#fff',
                                    fontWeight: 700,
                                    fontSize: { xs: '0.9rem', md: '1rem' },
                                    px: 2,
                                    py: 2.5,
                                    border: '1px solid rgba(255, 255, 255, 0.3)',
                                    backdropFilter: 'blur(10px)',
                                    '& .MuiChip-label': {
                                        px: 1
                                    }
                                }}
                            />
                            <Typography
                                sx={{
                                    fontSize: { xs: '1rem', md: '1.15rem' },
                                    color: 'rgba(255, 255, 255, 0.95)',
                                    textAlign: 'center',
                                    maxWidth: '700px',
                                    mt: 1,
                                    lineHeight: 1.6
                                }}
                            >
                                This certificate has been officially verified and authenticated by our system.
                                All details are accurate and up to date.
                            </Typography>
                        </Stack>
                    </Stack>
                </Container>
            </Box>

            {/* Certificate Details Section */}
            <Box
                sx={{
                    py: { xs: 5, md: 8 },
                    background: '#fff'
                }}
            >
                <Container maxWidth="md">
                    <Stack spacing={4}>
                        {/* Section Header */}
                        <Box sx={{ textAlign: 'center' }}>
                            <Typography
                                sx={{
                                    fontSize: { xs: '1.5rem', md: '2rem' },
                                    fontWeight: 700,
                                    color: theme.palette.text.primary,
                                    mb: 0.5
                                }}
                            >
                                Certificate Details
                            </Typography>
                            <Typography
                                sx={{
                                    fontSize: '0.95rem',
                                    color: theme.palette.text.secondary
                                }}
                            >
                                Official certificate information
                            </Typography>
                        </Box>

                        {/* Certificate Number */}
                        <Paper
                            elevation={0}
                            sx={{
                                background: `linear-gradient(135deg, ${theme.palette.primary.main}, ${theme.palette.primary.dark})`,
                                borderRadius: 2,
                                p: 3,
                                textAlign: 'center'
                            }}
                        >
                            <Typography
                                sx={{
                                    fontSize: '0.75rem',
                                    fontWeight: 600,
                                    color: 'rgba(255, 255, 255, 0.8)',
                                    textTransform: 'uppercase',
                                    letterSpacing: '1px',
                                    mb: 1
                                }}
                            >
                                Certificate Number
                            </Typography>
                            <Typography
                                sx={{
                                    fontSize: { xs: '1.2rem', md: '1.4rem' },
                                    fontWeight: 700,
                                    color: '#fff'
                                }}
                            >
                                {info.certificate_no || 'N/A'}
                            </Typography>
                        </Paper>

                        {/* Information Cards - Compact Grid */}
                        <Box
                            sx={{
                                display: 'grid',
                                gridTemplateColumns: {
                                    xs: '1fr',
                                    md: 'repeat(2, 1fr)'
                                },
                                gap: 2
                            }}
                        >
                            {[
                                {
                                    icon: Person,
                                    label: "Student Name",
                                    value: info.name,
                                    color: theme.palette.primary.main
                                },
                                {
                                    icon: Person,
                                    label: "Father's Name",
                                    value: info.guardian,
                                    color: theme.palette.info.main
                                },
                                {
                                    icon: People,
                                    label: "Mother's Name",
                                    value: info.mother,
                                    color: theme.palette.secondary.main
                                },
                                {
                                    icon: Cake,
                                    label: "Date of Birth",
                                    value: info.birth,
                                    color: theme.palette.error.main
                                },
                                {
                                    icon: School,
                                    label: "Course",
                                    value: `${info.c_title} / ${info.c_name}`,
                                    color: theme.palette.warning.main
                                },
                                {
                                    icon: CalendarToday,
                                    label: "Issue Date",
                                    value: info.issued_on,
                                    color: theme.palette.success.main
                                },
                                {
                                    icon: LocationOn,
                                    label: "Study Center",
                                    value: info.fr_name,
                                    color: theme.palette.info.dark,
                                    gridColumn: { xs: '1', md: '1 / -1' }
                                }
                            ].map((item, index) => {
                                const Icon = item.icon;
                                return (
                                    <Paper
                                        key={index}
                                        elevation={0}
                                        sx={{
                                            border: `1px solid ${theme.palette.divider}`,
                                            borderRadius: 2,
                                            p: 2.5,
                                            transition: 'all 0.2s ease',
                                            gridColumn: item.gridColumn,
                                            '&:hover': {
                                                borderColor: item.color,
                                                boxShadow: `0 4px 12px ${item.color}15`
                                            }
                                        }}
                                    >
                                        <Stack direction="row" spacing={2} alignItems="center">
                                            <Box
                                                sx={{
                                                    width: 40,
                                                    height: 40,
                                                    borderRadius: 1.5,
                                                    background: `${item.color}10`,
                                                    display: 'flex',
                                                    alignItems: 'center',
                                                    justifyContent: 'center',
                                                    flexShrink: 0
                                                }}
                                            >
                                                <Icon sx={{ fontSize: '1.25rem', color: item.color }} />
                                            </Box>
                                            <Box sx={{ minWidth: 0, flexGrow: 1 }}>
                                                <Typography
                                                    sx={{
                                                        fontSize: '0.7rem',
                                                        fontWeight: 600,
                                                        color: theme.palette.text.secondary,
                                                        textTransform: 'uppercase',
                                                        letterSpacing: '0.5px',
                                                        mb: 0.3
                                                    }}
                                                >
                                                    {item.label}
                                                </Typography>
                                                <Typography
                                                    sx={{
                                                        fontSize: '0.95rem',
                                                        fontWeight: 600,
                                                        color: theme.palette.text.primary,
                                                        wordBreak: 'break-word'
                                                    }}
                                                >
                                                    {item.value || 'N/A'}
                                                </Typography>
                                            </Box>
                                        </Stack>
                                    </Paper>
                                );
                            })}
                        </Box>

                        {/* Verification Badge - Compact */}
                        <Paper
                            elevation={0}
                            sx={{
                                background: `${theme.palette.success.main}08`,
                                border: `2px solid ${theme.palette.success.main}`,
                                borderRadius: 2,
                                p: 2.5
                            }}
                        >
                            <Stack direction="row" spacing={2} alignItems="center" justifyContent="center">
                                <Box
                                    sx={{
                                        width: 50,
                                        height: 50,
                                        borderRadius: '50%',
                                        background: theme.palette.success.main,
                                        display: 'flex',
                                        alignItems: 'center',
                                        justifyContent: 'center'
                                    }}
                                >
                                    <VerifiedUser sx={{ fontSize: '1.75rem', color: '#fff' }} />
                                </Box>
                                <Box>
                                    <Typography
                                        sx={{
                                            fontSize: '1.1rem',
                                            fontWeight: 700,
                                            color: theme.palette.text.primary,
                                            mb: 0.2
                                        }}
                                    >
                                        Verified by SpyWares
                                    </Typography>
                                    <Typography
                                        sx={{
                                            fontSize: '0.85rem',
                                            color: theme.palette.text.secondary
                                        }}
                                    >
                                        Officially verified and authenticated
                                    </Typography>
                                </Box>
                            </Stack>
                        </Paper>
                    </Stack>
                </Container>
            </Box>


        </>
    );
}
